import React, { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Autocomplete, Box, FormControl, MenuItem, Select, TextField } from '@mui/material'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { isNil } from 'lodash'

import { useStateContext } from '../../context/ContextProvider'
import axios from '../../lib/axios'
import Loading from '../../components/Loading'
import { isValid, handleAxiosError } from '../../helpers/helper'
import { TEXTFIELD_STYLE } from '../../constants/constants'

const ShowWorkCenter = () => {
    const { t } = useTranslation()
    const { config, pusher } = useStateContext()
    const params = useParams()
    const navigate = useNavigate()
    const location = useLocation()

    const [isLoading, setIsLoading] = useState(false)
    const [sites, setSites] = useState([])
    const [typeOptions, setTypeOptions] = useState([])
    const [statusOptions, setStatusOptions] = useState([])

    /* form */
    const [code, setCode] = useState('')
    const [name, setName] = useState('')
    const [siteId, setSiteId] = useState('')
    const [siteName, setSiteName] = useState('')
    const [type, setType] = useState('')
    const [description, setDescription] = useState('')
    const [availableHours, setAvailableHours] = useState('')
    const [costPerHour, setCostPerHour] = useState('')
    const [status, setStatus] = useState('')

    const goBack = () => {
        navigate((!isNil(location.state) && !isNil(location.state.prevPathname)) ? location.state.prevPathname : '/work-center')
    }

    useEffect(() => {
        getWorkCenter()
    }, [params.id])

    /* real-time: refresh if another user updates this work center, leave if they delete it */
    useEffect(() => {
        const clientId = localStorage.getItem('client_id')
        const channelUpdated = pusher.subscribe(`${clientId}-workcenter-updated`)
        const channelDeleted = pusher.subscribe(`${clientId}-workcenter-deleted`)

        channelUpdated.bind(`${clientId}-workcenter-updated-event`, data => {
            if (Number(data.id) === Number(params.id)) {
                getWorkCenter()
            }
        })

        channelDeleted.bind(`${clientId}-workcenter-deleted-event`, data => {
            if (Number(data.id) === Number(params.id)) {
                goBack()
            }
        })

        return (() => {
            pusher.unsubscribe(`${clientId}-workcenter-updated`)
            pusher.unsubscribe(`${clientId}-workcenter-deleted`)
        })
    }, [])

    useEffect(() => {
        getTypeOptions()
        getStatusOptions()
    }, [])

    const getWorkCenter = async () => {
        setIsLoading(true)

        await axios.get(`/api/work-centers/${params.id}`, config).then(res => {
            const workCenter = res.data?.data

            setCode(workCenter?.work_center_code ?? '')
            setName(workCenter?.work_center_name ?? '')
            setSiteId(workCenter?.site_id ?? '')
            setSiteName(workCenter?.site_name ?? '')
            setType(workCenter?.work_center_type ?? '')
            setDescription(workCenter?.description ?? '')
            setAvailableHours(isValid(workCenter?.available_hours) ? String(workCenter.available_hours) : '')
            setCostPerHour(isValid(workCenter?.cost_per_hour) ? String(workCenter.cost_per_hour) : '')
            setStatus(workCenter?.status ?? '')

            if (workCenter?.site_id) {
                setSites([{ id: workCenter.site_id, name: workCenter.site_name }])
            }
        }).catch(({ response }) => {
            handleAxiosError({ response: response })
        }).finally(() => {
            setIsLoading(false)
        })
    }

    const getTypeOptions = async () => {
        await axios
            .get('/api/work-center-types', config)
            .then(res => {
                setTypeOptions(res.data?.data ?? [])
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getStatusOptions = async () => {
        await axios
            .get('/api/work-center-statuses', config)
            .then(res => {
                setStatusOptions(res.data?.data ?? [])
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const optionsSites = sites?.map(option => ({ label: option.name, id: option.id }))

    return (
        <>
            {isLoading ? <Loading /> : ''}
            <Box sx={{ background: '#F5F5FD', minHeight: '100vh' }}>
                <div className='flex justify-between items-center p-5 w-full bg-white' style={{ borderRadius: '5px 5px 0 0' }}>
                    <div className='flex items-center justify-between w-full leading-none'>
                        <p className='text-[18px] font-[600] text-[#333333] flex items-center'><span className='text-[#1ab800] text-[35px] mr-4'>•</span> {t('view_x', { x: t('work_center') })} - {code}</p>
                        <div style={{ transform: "rotate(45deg)" }} className="font-[600] text-[#333333]">
                            <button onClick={goBack}><i className="fa-solid fa-plus"></i></button>
                        </div>
                    </div>
                </div>

                <div className='p-5 pb-12'>
                    <div className='bg-white p-6 pt-0 rounded-md w-full h-fit edit-view mb-3'>
                        <div className='pb-11 pt-1'>
                            <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{t('general_information')}</p>
                        </div>

                        <div className='grid grid-cols-1 lg:grid-cols-3 gap-x-5'>
                            {/* Code */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('code')}</p>
                                <TextField
                                    disabled
                                    value={code}
                                    sx={TEXTFIELD_STYLE}
                                />
                            </div>

                            {/* Name */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('name')}</p>
                                <TextField
                                    disabled
                                    value={name}
                                    sx={TEXTFIELD_STYLE}
                                />
                            </div>

                            {/* Site */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('site')}</p>
                                <Autocomplete
                                    disabled
                                    disablePortal
                                    disableClearable
                                    id="work-center-site-autocomplete"
                                    options={optionsSites}
                                    value={siteId ? { label: siteName, id: siteId } : null}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    sx={{
                                        width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                        boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                    }}
                                    renderInput={(params) => <TextField {...params} />}
                                />
                            </div>

                            {/* Type */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('type')}</p>
                                <FormControl sx={{ width: '100%' }}>
                                    <Select
                                        disabled
                                        displayEmpty
                                        value={type}
                                        sx={TEXTFIELD_STYLE}
                                    >
                                        <MenuItem value="" disabled>{t('select_x', { x: t('type') })}</MenuItem>
                                        {typeOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>

                            {/* Available Hours */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('available_hours')}</p>
                                <TextField
                                    disabled
                                    value={availableHours}
                                    sx={TEXTFIELD_STYLE}
                                />
                            </div>

                            {/* Cost / Hour */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('cost_per_hour')}</p>
                                <TextField
                                    disabled
                                    value={costPerHour}
                                    sx={TEXTFIELD_STYLE}
                                />
                            </div>

                            {/* Status */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('status')}</p>
                                <FormControl sx={{ width: '100%' }}>
                                    <Select
                                        disabled
                                        displayEmpty
                                        value={status}
                                        sx={TEXTFIELD_STYLE}
                                    >
                                        <MenuItem value="" disabled>{t('select_x', { x: t('status') })}</MenuItem>
                                        {statusOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>

                            {/* Description */}
                            <div className='w-full pb-4 lg:col-span-3'>
                                <p className="text-[14px] text-[#718096]">{t('description')}</p>
                                <TextField
                                    disabled
                                    multiline
                                    rows={3}
                                    value={description}
                                    sx={TEXTFIELD_STYLE}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Box>
        </>
    )
}

export default ShowWorkCenter
